import {
  useLocation,
  useMatches,
  useParams,
  useRouterState,
} from '@tanstack/react-router'
import { RecentRouteStorage } from 'features/navigation/recentRoutes'
import { useSelectedOrgAndSiteInterface } from 'features/orgAndSiteSourceOfTruth'
import { useGeckoPermissions } from 'features/permissions'
import { useEffect, useRef } from 'react'
import { RecentRoute } from 'types'

/**
 * Debounce delay in milliseconds to wait before saving a route.
 * This prevents saving intermediate routes during redirects,
 * as even the router status can be idle during these quick redirects.
 */
const ROUTE_SAVE_DEBOUNCE_MS = 500

export function useTrackRecentRoutes() {
  const location = useLocation()
  const matches = useMatches()
  const params = useParams({ strict: false })
  const { user } = useGeckoPermissions()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { status: routerStatus } = useRouterState()

  const { getSiteId } = useSelectedOrgAndSiteInterface()

  useEffect(() => {
    if (!RecentRouteStorage.isStorageAvailable()) {
      return
    }

    // Need both user ID and site ID to save routes
    const userId = user?.id
    const siteId = getSiteId() ?? params.siteID

    if (!userId || !siteId) {
      return
    }

    if (routerStatus !== 'idle') {
      return
    }

    // Clear any pending save operations
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      const lastMatch = matches.at(-1)
      const staticPageData = lastMatch?.staticData
      const pageTitle =
        staticPageData && 'title' in staticPageData
          ? (staticPageData?.title as string | undefined)
          : undefined

      const routeToSave: Omit<RecentRoute, 'timestamp'> = {
        pathname: lastMatch?.pathname ?? location.pathname,
        search: location.search
          ? (location.search as Record<string, unknown>)
          : undefined,
        title: pageTitle,
      }

      RecentRouteStorage.addRecentRoute(routeToSave, userId, siteId)
    }, ROUTE_SAVE_DEBOUNCE_MS)

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [
    location.pathname,
    location.search,
    matches,
    user,
    params.siteID,
    routerStatus,
    getSiteId,
  ])
}
